import { useState, useEffect, useMemo, useCallback } from 'react';
export { bidetsHandle as handle } from '../routeMetadata';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useProducts } from '../contexts/ProductContext';
import { Button } from '../components/Button';
import { TAN_BG } from '../data/constants';
import { BIDET_TIERS } from '../data/constants';
import { DisplayImage } from '../components/DisplayImage';
import { useOrder, useOrderRoute } from '../contexts/OrderContext';
import { FEATURE_FIELD_MAP } from '../data/productService';

// ─── Types ───────────────────────────────────────────────────────────────────

interface BidetImage {
  id: string;
  category: string;
  isMain: boolean;
  originalSource: string;
  localPath: string;
  filename: string;
}

interface BidetVariation {
  id: string;
  color: string;
  style: string;
  msrp: number;
  resellerUrl: string;
  resellerPrice: number;
  resellerShippingDays: number;
  passthroughTax: boolean;
  isDefaultVariation: boolean;
  brandUrl: string;
  images: BidetImage[];
}

interface BidetModel {
  id: string;
  Title?: string;
  'Model Name'?: string;
  Model?: string;
  'Product ID': string;
  images?: BidetImage[];
  manufacturer?: string;
  variations?: BidetVariation[];
  [key: string]: any;
}

type CardSize = 'main' | 'small';

interface FilterState {
  tier: string[];
  category: string[];
  brand: string[];
  priceMin: number | null;
  priceMax: number | null;
  features: string[];
  // Catalog-rebuild-v2: default-on filter that hides products where every
  // variation is `listed_only` or `discontinued`. Toggle off to see the
  // full catalog including products we don't install.
  installableOnly: boolean;
}

const DEFAULT_FILTERS: FilterState = {
  tier: [],
  category: [],
  brand: [],
  priceMin: null,
  priceMax: null,
  features: [],
  installableOnly: true,
};

// ─── Feature groups for filter UI ────────────────────────────────────────────
// Updated for catalog-rebuild-v2 — only buyer-decision features are shown.
// Boolean columns are filtered as "must have"; enum columns (power_source,
// water_heating_method, primary_control_type) get their own filters
// elsewhere.
//
// The display labels match FEATURE_FIELD_MAP in productService.ts so that
// filtering by feature label correctly looks up the boolean column on the
// product.

const FEATURE_GROUPS = [
  {
    label: 'Comfort',
    features: [
      'Heated seat',
      'Warm air dryer',
      'Adjustable water temperature',
    ],
  },
  {
    label: 'Wash modes',
    features: [
      'Feminine wash',
      'Oscillation',
      'Pulse / massage',
      'Turbo / vortex wash',
      'Pre-rinse',
    ],
  },
  {
    label: 'Nozzle',
    features: [
      'Self-cleaning nozzle',
      'Adjustable nozzle position',
      'Adjustable spray pressure',
    ],
  },
  {
    label: 'Hygiene',
    features: [
      'Deodorizer',
      'UV sterilization',
    ],
  },
  {
    label: 'Convenience',
    features: [
      'Soft-close seat',
      'Auto-open seat',
      'Auto-close seat',
      'Quick-release for cleaning',
      'Programmable presets',
      'Night light',
      'Companion mobile app',
    ],
  },
  {
    label: 'Accessibility & quality',
    features: [
      'Left-handed friendly',
      'ADA-compliant',
      'Lifetime warranty',
      'Commercial-grade',
    ],
  },
];

// Updated to match the new product_type enum values. Display strings are the
// human-friendly labels; comparison against the underlying column happens via
// `productTypeFromLabel` below.
const BIDET_CATEGORIES = [
  'Seat replacement bidet',
  'Under-seat attachment',
  'Handheld sprayer',
  'Integrated bidet toilet',
  'Ceramic bidet (with faucet)',
  'Bidet faucet only',
];

const PRODUCT_TYPE_LABEL_TO_ENUM: Record<string, string> = {
  'Seat replacement bidet': 'seat_replacement_bidet',
  'Under-seat attachment': 'under_seat_attachment',
  'Handheld sprayer': 'handheld_sprayer',
  'Integrated bidet toilet': 'integrated_bidet_toilet',
  'Ceramic bidet (with faucet)': 'ceramic_bidet_with_faucet',
  'Bidet faucet only': 'bidet_faucet_only',
};

// ─── Shared helpers ──────────────────────────────────────────────────────────

const getTitle = (model: BidetModel) => model.Title || model['Model Name'] || model.Model || '';

const getDefaultVariation = (model: BidetModel) =>
  model.variations?.find(v => v.isDefaultVariation) || model.variations?.[0];

const renderBidetCard = (
  model: BidetModel,
  size: CardSize,
  openBidetModal: (bidetId: string) => void
) => {
  const defaultVariation = getDefaultVariation(model);
  const displayPrice = defaultVariation?.resellerPrice;

  const allImages: BidetImage[] = [
    ...(model.images || []),
    ...(defaultVariation?.images || []),
  ];

  return (
    <Button
      key={model.id}
      type="button"
      variant="secondary"
      className={`bg-white p-4 rounded-lg shadow hover:shadow-lg transition flex flex-col items-center cursor-pointer focus:outline-none h-full w-full ${size === 'small' ? '' : ''}`}
      onClick={() => openBidetModal(model.id)}
      aria-label={`View details for ${getTitle(model)}`}
    >
      {allImages.length > 0 && (
        <DisplayImage
          images={allImages.map((image) => ({
            localPath: image.localPath,
            isMain: image.isMain
          }))}
          imgClassName={`mb-3 w-full ${size === 'small' ? 'w-full h-64 sm:h-96 md:h-full lg:h-64' : 'w-full h-24 xs:36 sm:h-64 h-96'}`}
          thumbnailsClickable={false}
          thumbnailImgClassName="w-4"
        />
      )}
      <h2 className={`font-bold mt-2 mb-1 text-center ${size === 'small' ? 'text-base' : 'text-lg'}`}>{getTitle(model)}</h2>
      <p className="text-blue-600 font-semibold mb-1">
        {displayPrice ? `from $${displayPrice}` : ''}
      </p>
      {model.variations && model.variations.length > 1 && (
        <p className="text-xs text-gray-500 mb-1">Multiple options available</p>
      )}
      <div className="text-sm text-gray-600 mb-1 text-center">{model.manufacturer}</div>
    </Button>
  );
};

// ─── Filter helpers ──────────────────────────────────────────────────────────

function parseFiltersFromUrl(param: string | null): FilterState {
  if (!param) return DEFAULT_FILTERS;
  try {
    const parsed = JSON.parse(decodeURIComponent(param));
    return {
      tier: Array.isArray(parsed.tier) ? parsed.tier : [],
      category: Array.isArray(parsed.category) ? parsed.category : [],
      brand: Array.isArray(parsed.brand) ? parsed.brand : [],
      priceMin: typeof parsed.priceMin === 'number' ? parsed.priceMin : null,
      priceMax: typeof parsed.priceMax === 'number' ? parsed.priceMax : null,
      features: Array.isArray(parsed.features) ? parsed.features : [],
      // Default ON; only flip if the URL explicitly sets it to false.
      installableOnly: parsed.installableOnly !== false,
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

function filtersToUrlParam(filters: FilterState): string | null {
  const hasFilters =
    filters.tier.length > 0 ||
    filters.category.length > 0 ||
    filters.brand.length > 0 ||
    filters.priceMin !== null ||
    filters.priceMax !== null ||
    filters.features.length > 0 ||
    filters.installableOnly === false; // serialise only when toggled OFF
  if (!hasFilters) return null;
  return encodeURIComponent(JSON.stringify(filters));
}

function countActiveFilters(filters: FilterState): number {
  let count = 0;
  count += filters.tier.length;
  count += filters.category.length;
  count += filters.brand.length;
  if (filters.priceMin !== null) count++;
  if (filters.priceMax !== null) count++;
  count += filters.features.length;
  // Only count installableOnly when toggled OFF (the non-default state).
  if (filters.installableOnly === false) count++;
  return count;
}

// ─── Page component ──────────────────────────────────────────────────────────

export default function BidetsPage() {
  const { view } = useParams();
  const navigate = useNavigate();
  const orderRoute = useOrderRoute();
  const [searchParams, setSearchParams] = useSearchParams();
  const { bidetModalOpen, selectedBidetIdForBidetModal, openBidetModal, closeBidetModal } = useOrder();

  const isAllView = view === 'all';
  const isValidView = !view || view === 'recommended' || view === 'all';

  // Redirect invalid view values
  useEffect(() => {
    if (!isValidView) {
      navigate('/bidets/recommended', { replace: true });
    }
  }, [isValidView, navigate]);

  // Auto-open modal from URL on mount
  useEffect(() => {
    const selectedBidet = searchParams.get('selected-bidet');
    if (selectedBidet && !bidetModalOpen) {
      openBidetModal(selectedBidet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync modal state → URL
  useEffect(() => {
    const currentParam = searchParams.get('selected-bidet');
    if (bidetModalOpen && selectedBidetIdForBidetModal) {
      if (currentParam !== selectedBidetIdForBidetModal) {
        setSearchParams(prev => {
          prev.set('selected-bidet', selectedBidetIdForBidetModal);
          return prev;
        }, { replace: true });
      }
    } else if (!bidetModalOpen && currentParam) {
      setSearchParams(prev => {
        prev.delete('selected-bidet');
        return prev;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidetModalOpen, selectedBidetIdForBidetModal]);

  return (
    <main
      style={{ backgroundColor: TAN_BG }}
      className="py-2"
    >
      <Header isAllView={isAllView} />

      {isAllView ? (
        <AllBidetsView />
      ) : (
        <>
          <FavoriteStarterBidets />

          <p className="py-8 text-center text-lg md:text-xl max-w-2xl mx-auto text-gray-700 px-4">
            🎉 Pretty cool, right? This next section covers
            {' '}
            <b>"mid-range"</b>
            {' '}
            bidets, which all replace your existing toilet seat and offer heated seats, as well as bidet functions, without breaking the bank.  🚽✨
          </p>

          <FavoriteMidrangeBidets />

          <p className="py-8 text-center text-lg md:text-xl max-w-2xl mx-auto text-gray-700 px-4">
            😏 Still not impressed? Well, as long as you have the budget, there's going to be something you love below. Get ready for some bells and whistles. 🛁🔔
          </p>

          <FavoriteFullFeatureBidets />

          <PickAnotherBidet />
        </>
      )}
    </main>
  );
}

// ─── Header with ViewToggle ──────────────────────────────────────────────────

function Header({ isAllView }: { isAllView: boolean }) {
  return (
    <div className="container mx-auto px-4">
      <div className="px-4">
        <section className="w-full">
          <div className="flex flex-col gap-4 items-center justify-center min-h-[200px]">
            <div className="text-center">
              <h1 className="text-3xl font-semibold mb-4 text-black">
                {isAllView ? 'All Bidets' : 'Recommended Bidets'}
              </h1>
              <p className="text-lg text-black text-base mb-4">
                {isAllView
                  ? 'Browse our full catalog of bidets. Use filters to find exactly what you need.'
                  : (
                    <>
                      There are
                      {' '}
                      <i><b>thousands</b></i>
                      {' '}
                      of bidets out there. This curated collection has the best of them all.
                    </>
                  )
                }
              </p>
            </div>
            <ViewToggle isAllView={isAllView} />
          </div>
        </section>
      </div>
    </div>
  );
}

// ─── ViewToggle (pill segmented control) ─────────────────────────────────────

function ViewToggle({ isAllView }: { isAllView: boolean }) {
  const navigate = useNavigate();

  return (
    <div className="flex items-center bg-gray-200 rounded-full p-1 mb-4">
      <button
        type="button"
        onClick={() => navigate('/bidets/recommended')}
        className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
          !isAllView
            ? 'bg-blue-600 text-white shadow-sm'
            : 'text-gray-600 hover:text-gray-800'
        }`}
      >
        Recommended
      </button>
      <button
        type="button"
        onClick={() => navigate('/bidets/all')}
        className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
          isAllView
            ? 'bg-blue-600 text-white shadow-sm'
            : 'text-gray-600 hover:text-gray-800'
        }`}
      >
        All Bidets
      </button>
    </div>
  );
}

// ─── All Bidets View ─────────────────────────────────────────────────────────

function AllBidetsView() {
  const { products } = useProducts();
  const { openBidetModal } = useOrder();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<FilterState>(() =>
    parseFiltersFromUrl(searchParams.get('filters'))
  );
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Sync filters → URL
  useEffect(() => {
    const param = filtersToUrlParam(filters);
    setSearchParams(prev => {
      if (param) {
        prev.set('filters', param);
      } else {
        prev.delete('filters');
      }
      return prev;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Get all live bidet products. p['Category'] is the new product_type enum
  // value; BIDET_CATEGORIES is the human-friendly label list. Convert via
  // PRODUCT_TYPE_LABEL_TO_ENUM and compare against the underlying enum.
  const allBidets = useMemo(() => {
    const validEnums = new Set(
      BIDET_CATEGORIES.map(label => PRODUCT_TYPE_LABEL_TO_ENUM[label]).filter(Boolean)
    );
    return products.filter((p: any) => validEnums.has(p['Category']));
  }, [products]);

  // Get available brands for filter options
  const availableBrands = useMemo(() => {
    const brands = new Set<string>();
    allBidets.forEach((b: any) => {
      if (b['Brand']) brands.add(b['Brand']);
    });
    return Array.from(brands).sort();
  }, [allBidets]);

  // Apply filters
  const filteredBidets = useMemo(() => {
    return allBidets.filter((b: any) => {
      // Install-status filter (default ON). Hide products where every
      // variation is `listed_only` or `discontinued` — i.e. nothing
      // BNY can actually install. Per Jacq: never hide a bidet, but
      // make the default view focused on what we offer.
      if (filters.installableOnly) {
        const variations = b.variations || [];
        const anyInstallable = variations.some((v: any) =>
          (v.bnyInstallStatus || 'installable') === 'installable'
        );
        if (!anyInstallable) return false;
      }

      // Tier filter
      if (filters.tier.length > 0 && !filters.tier.includes(b['Tier'])) return false;

      // Category filter
      // Filter by product_type. The filter UI shows human-friendly labels but
      // the underlying column stores enum values, so convert before compare.
      if (filters.category.length > 0) {
        const wantedEnumValues = filters.category.map(label => PRODUCT_TYPE_LABEL_TO_ENUM[label] ?? label);
        if (!wantedEnumValues.includes(b['Category'])) return false;
      }

      // Brand filter
      if (filters.brand.length > 0 && !filters.brand.includes(b['Brand'])) return false;

      // Price range filter (based on default variation reseller price)
      const defaultVar = getDefaultVariation(b);
      const price = defaultVar?.resellerPrice;
      if (filters.priceMin !== null && (price === undefined || price < filters.priceMin)) return false;
      if (filters.priceMax !== null && (price === undefined || price > filters.priceMax)) return false;

      // Feature filters (all selected features must be `true`).
      // Catalog-rebuild-v2: feature columns are now real booleans, not the
      // legacy 'yes' / 'no' / 'YTBD' tri-value strings.
      for (const featureLabel of filters.features) {
        // Find the snake_case column name from the label, then check the
        // boolean on the product. Fall back to legacy 'yes' check for the
        // brief transition window where some products may not have been
        // re-seeded yet.
        const columnName = Object.entries(FEATURE_FIELD_MAP).find(
          ([, label]) => label === featureLabel
        )?.[0];
        if (!columnName) return false;
        const value = (b as Record<string, unknown>)[columnName] ?? b[featureLabel];
        if (value !== true && value !== 'yes') return false;
      }

      return true;
    });
  }, [allBidets, filters]);

  const updateFilters = useCallback((updates: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...updates }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const activeFilterCount = countActiveFilters(filters);

  return (
    <div className="container mx-auto px-4 mb-4">
      {/* Mobile filter toggle */}
      <div className="lg:hidden mb-4">
        <Button
          type="button"
          variant="secondary"
          className="w-full flex items-center justify-center gap-2"
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Filter sidebar */}
        <div className={`${filtersOpen ? 'block' : 'hidden'} lg:block w-full lg:w-72 flex-shrink-0`}>
          <BidetFilterBar
            filters={filters}
            updateFilters={updateFilters}
            clearFilters={clearFilters}
            activeFilterCount={activeFilterCount}
            availableBrands={availableBrands}
            onClose={() => setFiltersOpen(false)}
          />
        </div>

        {/* Product grid */}
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-4">
            Showing {filteredBidets.length} bidet{filteredBidets.length !== 1 ? 's' : ''}
          </p>

          {filteredBidets.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center">
              <p className="text-gray-600 mb-4">No bidets match your filters.</p>
              <Button type="button" variant="primary" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBidets.map((model: any) => renderBidetCard(model, 'small', openBidetModal))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Filter Bar ──────────────────────────────────────────────────────────────

interface BidetFilterBarProps {
  filters: FilterState;
  updateFilters: (updates: Partial<FilterState>) => void;
  clearFilters: () => void;
  activeFilterCount: number;
  availableBrands: string[];
  onClose: () => void;
}

function BidetFilterBar({
  filters,
  updateFilters,
  clearFilters,
  activeFilterCount,
  availableBrands,
  onClose,
}: BidetFilterBarProps) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (label: string) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const toggleArrayFilter = (key: keyof FilterState, value: string) => {
    const current = filters[key] as string[];
    if (current.includes(value)) {
      updateFilters({ [key]: current.filter(v => v !== value) });
    } else {
      updateFilters({ [key]: [...current, value] });
    }
  };

  const toggleFeature = (feature: string) => {
    if (filters.features.includes(feature)) {
      updateFilters({ features: filters.features.filter(f => f !== feature) });
    } else {
      updateFilters({ features: [...filters.features, feature] });
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg">Filters</h3>
        <div className="flex items-center gap-2">
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:underline"
            >
              Clear all
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden text-gray-500 hover:text-gray-700 text-xl leading-none"
            aria-label="Close filters"
          >
            &times;
          </button>
        </div>
      </div>

      {/* Install availability — default ON */}
      <FilterSection title="Availability">
        <CheckboxItem
          label="Only what we install"
          checked={filters.installableOnly}
          onChange={() => updateFilters({ installableOnly: !filters.installableOnly })}
        />
        <p className="text-xs text-gray-500 mt-1 leading-snug">
          Untoggle to also see bidets we list but don't install ourselves
          (e.g. dual-temp models that need new hot-water plumbing).
        </p>
      </FilterSection>

      {/* Tier */}
      <FilterSection title="Tier">
        {BIDET_TIERS.map(tier => (
          <CheckboxItem
            key={tier}
            label={tier}
            checked={filters.tier.includes(tier)}
            onChange={() => toggleArrayFilter('tier', tier)}
          />
        ))}
      </FilterSection>

      {/* Category */}
      <FilterSection title="Category">
        {BIDET_CATEGORIES.map(cat => (
          <CheckboxItem
            key={cat}
            label={cat.replace(/\b\w/g, c => c.toUpperCase())}
            checked={filters.category.includes(cat)}
            onChange={() => toggleArrayFilter('category', cat)}
          />
        ))}
      </FilterSection>

      {/* Brand */}
      {availableBrands.length > 0 && (
        <FilterSection title="Brand">
          {availableBrands.map(brand => (
            <CheckboxItem
              key={brand}
              label={brand}
              checked={filters.brand.includes(brand)}
              onChange={() => toggleArrayFilter('brand', brand)}
            />
          ))}
        </FilterSection>
      )}

      {/* Price Range */}
      <FilterSection title="Price Range">
        <div className="flex items-center gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filters.priceMin ?? ''}
            onChange={e => updateFilters({ priceMin: e.target.value ? Number(e.target.value) : null })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <span className="text-gray-400">-</span>
          <input
            type="number"
            placeholder="Max"
            value={filters.priceMax ?? ''}
            onChange={e => updateFilters({ priceMax: e.target.value ? Number(e.target.value) : null })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </FilterSection>

      {/* Feature groups */}
      {FEATURE_GROUPS.map(group => (
        <div key={group.label} className="border-t border-gray-100 py-3">
          <button
            type="button"
            onClick={() => toggleGroup(group.label)}
            className="flex items-center justify-between w-full text-left"
          >
            <span className="text-sm font-medium text-gray-700">{group.label}</span>
            <span className="text-gray-400 text-xs">
              {expandedGroups[group.label] ? '▲' : '▼'}
            </span>
          </button>
          {expandedGroups[group.label] && (
            <div className="mt-2 space-y-1">
              {group.features.map(feature => (
                <CheckboxItem
                  key={feature}
                  label={feature}
                  checked={filters.features.includes(feature)}
                  onChange={() => toggleFeature(feature)}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Filter UI primitives ────────────────────────────────────────────────────

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-gray-100 py-3">
      <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function CheckboxItem({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 hover:text-gray-800">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      {label}
    </label>
  );
}

// ─── Recommended view components (unchanged) ────────────────────────────────

interface RecommendedBidetSectionProps {
  sectionTitle: string;
  sectionDescription: string;
  mainCategory: string;
  additionalCategories?: string[];
  recommendedCategoriesMap?: Record<string, string>;
}

function RecommendedBidetSection({
  sectionTitle,
  sectionDescription,
  mainCategory,
  additionalCategories = [],
  recommendedCategoriesMap = {},
}: RecommendedBidetSectionProps) {
  const { openBidetModal } = useOrder();
  const { products: bidets } = useProducts();

  return (
    <div className="container mx-auto px-4 mb-4">
      <div className="bg-white py-8 px-8">
        <section className="w-full">
          <h2 className="text-2xl font-semibold mb-2 text-black">{sectionTitle}</h2>
          <p>{sectionDescription}</p>
          <div className="flex flex-col md:flex-row md:flex-nowrap gap-8 mt-6">
            {/* Main bidet card(s) */}
            <div className="w-full md:w-1/3 flex flex-wrap gap-6">
              {bidets.filter((b: any) => b['Recommended Category'] === mainCategory).map((model: any) => renderBidetCard(model, 'main', openBidetModal))}
            </div>
            {/* Additional recommended categories */}
            {additionalCategories.length > 0 && (
              <div className="w-full md:w-2/3">
                <h2 className="text-xl font-semibold mb-2 text-black">These are more specialized</h2>
                <div className="w-full flex flex-col md:flex-row gap-4 md:gap-6 mt-6 md:mt-0 bg-gray-100 rounded-lg p-4">
                  {additionalCategories.map((cat) => {
                    const model = bidets.find((b: any) => b['Recommended Category'] === cat);
                    if (!model) return null;
                    return (
                      <div key={cat} className="flex flex-col items-center w-full md:w-1/3 flex-1">
                        <h3 className="text-lg font-semibold mb-2 text-black">{recommendedCategoriesMap[cat]}</h3>
                        <div className="flex-1 w-full flex">
                          {renderBidetCard(model, 'small', openBidetModal)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function FavoriteStarterBidets() {
  const FAVORITE_STARTER_BIDET = 'Favorite Starter Bidet';
  const FAVORITE_STARTER_BIDET_FANCIER = 'Favorite Starter Bidet - Fancier';
  const FAVORITE_STARTER_BIDET_LEFTY = 'Favorite Starter Bidet - If Lefty';

  const recommendedCategories = {
    [FAVORITE_STARTER_BIDET]: 'Favorite Starter Bidet',
    [FAVORITE_STARTER_BIDET_FANCIER]: 'Fancier & Non-plastic',
    [FAVORITE_STARTER_BIDET_LEFTY]: 'For Lefties',
  };

  return (
    <RecommendedBidetSection
      sectionTitle="Our Favorite Starter Bidet"
      sectionDescription="Easy on the wallet, no frills, fits on just about any toilet, and provides a consistently good experience."
      mainCategory={FAVORITE_STARTER_BIDET}
      additionalCategories={[FAVORITE_STARTER_BIDET_FANCIER, FAVORITE_STARTER_BIDET_LEFTY]}
      recommendedCategoriesMap={recommendedCategories}
    />
  );
}

function FavoriteMidrangeBidets() {
  const FAVORITE_MID_RANGE_BIDET = 'Favorite Mid-Range Bidet';
  const FAVORITE_MID_RANGE_BIDET_REMOTE_CONTROLLED = 'Favorite Mid-Range Bidet - Remote Controlled';
  const FAVORITE_MID_RANGE_BIDET_NO_REMOTE = 'Favorite Mid-Range Bidet - No Remote';

  const recommendedCategories = {
    [FAVORITE_MID_RANGE_BIDET]: 'Favorite Mid-Range Bidet',
    [FAVORITE_MID_RANGE_BIDET_REMOTE_CONTROLLED]: 'Remote Controlled',
    [FAVORITE_MID_RANGE_BIDET_NO_REMOTE]: 'No Remote',
  };

  return (
    <RecommendedBidetSection
      sectionTitle="Our Favorite Midrange Bidet"
      sectionDescription="A great balance of features and price. These models offer more comfort and convenience than the basics."
      mainCategory={FAVORITE_MID_RANGE_BIDET}
      additionalCategories={[
        FAVORITE_MID_RANGE_BIDET_REMOTE_CONTROLLED,
        FAVORITE_MID_RANGE_BIDET_NO_REMOTE,
      ]}
      recommendedCategoriesMap={recommendedCategories}
    />
  );
}

function FavoriteFullFeatureBidets() {
  const FAVORITE_FULL_FEATURE_BIDET = 'Favorite Full-Feature Bidet';
  const FAVORITE_FULL_FEATURE_BIDET_REMOTE_CONTROLLED = 'Favorite Full-Feature Bidet - Remote Controlled';
  const FAVORITE_FULL_FEATURE_BIDET_COMMERCIAL = 'Favorite Full-Feature Bidet - Commercial';

  const recommendedCategories = {
    [FAVORITE_FULL_FEATURE_BIDET]: 'Favorite Full-Feature Bidet',
    [FAVORITE_FULL_FEATURE_BIDET_REMOTE_CONTROLLED]: 'Remote Controlled',
    [FAVORITE_FULL_FEATURE_BIDET_COMMERCIAL]: 'Commercial',
  };

  return (
    <RecommendedBidetSection
      sectionTitle="Our Favorite Full-Feature Bidet"
      sectionDescription="Top-of-the-line models with every feature you could want. Experience the ultimate in bidet luxury."
      mainCategory={FAVORITE_FULL_FEATURE_BIDET}
      additionalCategories={[
        FAVORITE_FULL_FEATURE_BIDET_REMOTE_CONTROLLED,
        FAVORITE_FULL_FEATURE_BIDET_COMMERCIAL,
      ]}
      recommendedCategoriesMap={recommendedCategories}
    />
  );
}

function PickAnotherBidet() {
  const navigate = useNavigate();
  const orderRoute = useOrderRoute();

  return (
    <div className="container mx-auto px-4 mb-4">
      <div className="bg-white py-8 px-4">
        <section className="w-full flex flex-col items-center text-center">
          <h2 className="text-2xl font-semibold mb-2 text-black">Don't like any of these? Pick another one.</h2>
          <p className="mb-6">We recommend these, but have more available on our ordering page. You're also welcome to contact us to arrange a specialized installation with a pre-purchased bidet.</p>
          <Button
            type="button"
            variant="primary"
            className="px-6 py-3 text-lg font-semibold"
            onClick={() => {
              navigate(orderRoute);
            }}
          >
            Order with another Bidet
          </Button>
        </section>
      </div>
    </div>
  );
}
